import { Platform } from 'react-native'

export type UploadFileAsset = {
  uri: string
  fileName?: Maybe<string>
  name?: Maybe<string>
  fileSize?: Maybe<number>
  mimeType?: string
}

type UploadToQiniuOptions = {
  token: string
  file: UploadFileAsset
  onProgress?: (progress: number) => void
  abortController?: AbortController
}

// Backend rejects file names longer than 200 characters (Attachment::MAX_FILE_NAME_LENGTH).
// Slice by code points so multi-byte characters (emoji, etc.) are never split, matching
// how Ruby counts String#length.
const MAX_FILE_NAME_LENGTH = 200

export function truncateFileName(name: Maybe<string> | undefined): string | undefined {
  if (!name) return undefined
  const chars = Array.from(name)
  if (chars.length <= MAX_FILE_NAME_LENGTH) return name
  const dotIndex = name.lastIndexOf('.')
  const ext = dotIndex > 0 ? name.slice(dotIndex) : ''
  const extLength = Array.from(ext).length
  if (extLength >= MAX_FILE_NAME_LENGTH) return chars.slice(0, MAX_FILE_NAME_LENGTH).join('')
  return chars.slice(0, MAX_FILE_NAME_LENGTH - extLength).join('') + ext
}

export type UploadToQiniuResponseType = {
  security_code: string
  content_type: string
  file_name: string
  file_size: number
  file_width: number
  file_height: number
  key: string
}

export async function uploadToQiniu({
  token,
  file,
  onProgress,
  abortController,
}: UploadToQiniuOptions): Promise<UploadToQiniuResponseType> {
  try {
    const url = 'https://upload.qiniup.com/'
    const fileName = truncateFileName(file.fileName || file.name)
    const formData = new FormData()
    if (Platform.OS === 'web') {
      // Web's W3C FormData requires a Blob; the RN `{ uri, name, type }` shape only works
      // under React Native's FormData polyfill. Fetch the picked URI into a real Blob.
      const blob = await fetch(file.uri).then(res => res.blob())
      formData.append('file', blob, fileName)
    } else {
      formData.append('file', { uri: file.uri, name: fileName, type: file.mimeType } as any, fileName)
    }
    formData.append('token', token)
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', url, true)

      xhr.upload.addEventListener('progress', event => {
        if (event.lengthComputable) {
          onProgress?.((event.loaded / event.total) * 100)
        }
      })

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'))
      })

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.response as string) as UploadToQiniuResponseType)
        } else if (xhr.status >= 400) {
          const error = JSON.parse(xhr.response as string) as { error?: string }
          reject(new Error(error.error || 'Upload failed'))
        } else {
          reject(new Error('Upload failed'))
        }
      })

      if (abortController) {
        const handleAbort = () => {
          xhr.abort()
          abortController.signal.removeEventListener('abort', handleAbort)
        }
        abortController.signal.addEventListener('abort', handleAbort)
      }

      xhr.send(formData)
    })
  } catch (e) {
    console.error(e)
    throw e
  }
}
